import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle } from "lucide-react";
import { coachListMessageThreads } from "../../lib/api";

function relativeTime(iso) {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (Number.isNaN(mins)) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

export default function CoachMessagesPage() {
  const [threads, setThreads] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    coachListMessageThreads()
      .then(setThreads)
      .catch(() => setThreads([]));
  }, []);

  const filtered = (threads || []).filter((t) =>
    t.name.toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[var(--tir-ivory)]" data-testid="coach-messages-page">
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-baseline justify-between mb-5">
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[var(--tir-ink)] tracking-wide">
            Messages
          </h1>
          <span className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)]">
            All athlete conversations
          </span>
        </div>
        <div className="relative mb-5">
          <Search
            size={15}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--tir-muted)]"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search athletes…"
            data-testid="messages-search-input"
            className="w-full border border-[var(--tir-border)] bg-white rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[var(--tir-burgundy)]"
          />
        </div>
        {threads === null ? (
          <div className="text-sm italic text-[var(--tir-muted)]">Loading conversations…</div>
        ) : filtered.length === 0 ? (
          <div
            className="bg-white rounded-md border-2 border-dashed border-[var(--tir-border)] py-12 text-center text-sm text-[var(--tir-muted)]"
            data-testid="messages-empty"
          >
            No athletes match.
          </div>
        ) : (
          <div className="bg-white rounded-md border border-[var(--tir-border)] divide-y divide-[var(--tir-border)] overflow-hidden">
            {filtered.map((t) => (
              <Link
                key={t.athlete_id}
                to={`/coaching/messages/${t.athlete_id}`}
                className="flex items-center gap-3.5 px-4 py-3.5 hover:bg-[var(--tir-ivory)] transition-colors"
                data-testid={`message-thread-row-${t.athlete_id}`}
              >
                <div className="shrink-0 w-11 h-11 rounded-full bg-[var(--tir-navy)]/10 text-[var(--tir-navy)] font-semibold text-sm flex items-center justify-center">
                  {initials(t.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className={`text-[14px] truncate ${
                        t.unread_count > 0
                          ? "font-bold text-[var(--tir-ink)]"
                          : "font-semibold text-[var(--tir-ink)]"
                      }`}
                    >
                      {t.name}
                    </span>
                    <span className="text-[10px] font-mono-tir text-[var(--tir-muted)] shrink-0">
                      {relativeTime(t.last_at)}
                    </span>
                  </div>
                  <div
                    className={`text-[12px] truncate mt-0.5 ${
                      t.unread_count > 0
                        ? "text-[var(--tir-ink)] font-medium"
                        : "text-[var(--tir-muted)]"
                    }`}
                  >
                    {t.last_body ? (
                      <>
                        {t.last_from_role === "coach" && (
                          <span className="text-[var(--tir-muted)]">You: </span>
                        )}
                        {t.last_body}
                      </>
                    ) : (
                      <span className="italic inline-flex items-center gap-1">
                        <MessageCircle size={11} /> Start a conversation
                      </span>
                    )}
                  </div>
                </div>
                {t.unread_count > 0 && (
                  <span
                    className="shrink-0 min-w-[20px] h-5 px-1.5 bg-[var(--tir-burgundy)] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    data-testid={`message-thread-unread-${t.athlete_id}`}
                  >
                    {t.unread_count > 9 ? "9+" : t.unread_count}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
